import { LayoutConfig } from '../../../models/user-config/DisplayModeConfig';
import IUUIDService from '../../uid/IUUIDService';
import UniqueId from '../../uid/UniqueId';
import UUIDService from '../../uid/UUIDService';
import DatasetsLayoutNode from '../dataset-tree/layout/DatasetsLayoutNode';
import DatasetsLayoutNodeVisitor from '../dataset-tree/layout/DatasetsLayoutNodeVisitor';
import HorizontalStackLayoutNode from '../dataset-tree/layout/HorizontalStackLayoutNode';
import SingleDatasetLayoutNode from '../dataset-tree/layout/SingleDatasetLayoutNode';
import TabsLayoutNode from '../dataset-tree/layout/TabsLayoutNode';
import VerticalStackLayoutNode from '../dataset-tree/layout/VerticalStackLayoutNode';
import { DatasetLayoutViewModelType } from '../view-model/DatasetLayoutViewModel';

import LayoutNodeDimensionVisitor from './LayoutNodeDimensionVisitor';
import TitleCollatorVisitor from './TitleCollatorVisitor';
import ViewModelCompositorDatasetVisitor from './ViewModelCompositorDatasetVisitor';
import ViewModelCompositorLayoutVisitorDelegate from './ViewModelCompositorLayoutVisitorDelegate';

/**
 * {@link DatasetsLayoutNode} tree visitor that manages the serialization of
 * layout elements tree items into final {@link DatasetLayoutViewModel} objects.
 */
class ViewModelCompositorLayoutVisitor implements DatasetsLayoutNodeVisitor {
  private readonly idService: IUUIDService;

  constructor(
    private childIds: UniqueId[],
    private delegate: ViewModelCompositorLayoutVisitorDelegate,
    private layoutConfig?: LayoutConfig
  ) {
    this.idService = new UUIDService();
  }

  public visitHorizontalStack(node: HorizontalStackLayoutNode): void {
    const { configWidth, initialScale } = this.getChildrenDimensions(
      node,
      'cols'
    );

    this.delegate.addLayoutViewModel({
      type: DatasetLayoutViewModelType.HORIZONTALLY_STACKED,
      id: this.idService.generateId(),
      children: this.childIds,
      initialScale: configWidth
        ? [configWidth, 100 - configWidth]
        : initialScale,
    });
  }

  public visitTabs(node: TabsLayoutNode): void {
    const titles: string[] = [];

    for (const tabNode of node.getChildren()) {
      const titleCollator = new TitleCollatorVisitor(this.delegate);

      tabNode.acceptVisitor(titleCollator);
      titles.push(titleCollator.getCollatedTitle() || '');
    }

    this.delegate.addLayoutViewModel({
      type: DatasetLayoutViewModelType.TABBED,
      id: this.idService.generateId(),
      children: this.childIds,
      titles,
    });
  }

  public visitVerticalStack(node: VerticalStackLayoutNode): void {
    const { initialScale, configHeight } = this.getChildrenDimensions(
      node,
      'rows'
    );

    this.delegate.addLayoutViewModel({
      type: DatasetLayoutViewModelType.VERTICALLY_STACKED,
      id: this.idService.generateId(),
      children: this.childIds,
      initialScale: configHeight
        ? [configHeight, 100 - configHeight]
        : initialScale,
    });
  }

  public visitSingleDataset(node: SingleDatasetLayoutNode): void {
    const datasetVisitor = new ViewModelCompositorDatasetVisitor(this.delegate);

    node.getDataset().acceptVisitor(datasetVisitor);

    const datasetViewModel = datasetVisitor.getConstructedViewModel();

    this.delegate.addLayoutViewModel({
      type: DatasetLayoutViewModelType.SINGLE,
      id: this.idService.generateId(),
      dataset: datasetViewModel.id,
    });
  }

  private getChildrenDimensions(
    node: DatasetsLayoutNode,
    axis: 'cols' | 'rows'
  ): { configHeight?: number; configWidth?: number; initialScale: number[] } {
    const dimensions = [];
    let configHeight;
    let configWidth;

    for (const child of node.getChildren()) {
      const dimensionVisitor = new LayoutNodeDimensionVisitor(
        this.layoutConfig
      );

      child.acceptVisitor(dimensionVisitor);

      configHeight = configHeight || dimensionVisitor.getConfigHeight();
      configWidth = configWidth || dimensionVisitor.getConfigWidth();

      let dimension;

      if (axis === 'cols') {
        dimension = dimensionVisitor.getLayoutCols();
      } else {
        dimension = dimensionVisitor.getLayoutRows();
      }

      dimensions.push(dimension);
    }

    return { configHeight, configWidth, initialScale: dimensions };
  }
}

export default ViewModelCompositorLayoutVisitor;
